import type { RailBranch, RailLayout } from "@/data/rail-branches";
import { LOCATION_BOX_STEP_PX } from "../../location/LocationMarker/stack";
import { MARKER_DIAMETER } from "../../marker/MarkerMetrics/metrics";
import { branchAt } from "../RailBranchMotion/motion";
import { markerRailPose } from "../RailPose/pose";
import { stayAt } from "../StayAt/stay";

/** Two location boxes under the upper rail, and space before the group below. */
const LOCATIONS_ROOM_PX = LOCATION_BOX_STEP_PX + 90;
/** So a very short split still reads as a split. */
const MIN_PITCH_PX = 260;

/** Branches on side rails that touch in time move apart together, so their curves meet. */
function splitEpisodes(branches: readonly RailBranch[]): RailBranch[][] {
  const sides = branches.filter((branch) => branch.lane !== 0)
    .sort((a, b) => a.startMs - b.startMs);
  const episodes: RailBranch[][] = [];
  let endMs = -Infinity;
  for (const branch of sides) {
    if (branch.startMs > endMs) {
      episodes.push([]);
    }
    episodes.at(-1)!.push(branch);
    endMs = Math.max(endMs, branch.endMs);
  }
  return episodes;
}

/** Moments to measure: the middle of everything that overlaps this stretch of time. */
function sampleTimes(rail: RailLayout, startMs: number, endMs: number): number[] {
  const edges = [startMs, endMs];
  for (const stay of rail.stays) {
    if (stay.startMs < endMs && startMs < stay.endMs) {
      edges.push(Math.max(stay.startMs, startMs), Math.min(stay.endMs, endMs));
    }
  }
  const sorted = [...new Set(edges)].sort((a, b) => a - b);
  return sorted.slice(1).map((end, index) => (sorted[index]! + end) / 2);
}

/** For each line, the tallest group on it while another line runs above it. */
function tallestGroupByLane(
  rail: RailLayout,
  startMs: number,
  endMs: number,
): Map<number, number> {
  const ids = [...new Set(rail.stays.map((stay) => stay.characterId))];
  const tallest = new Map<number, number>();
  for (const at of sampleTimes(rail, startMs, endMs)) {
    const running = rail.branches
      .filter((branch) => branch.startMs <= at && at < branch.endMs)
      .map((branch) => branch.lane);
    for (const id of ids) {
      const pose = markerRailPose(id, at, rail);
      const stay = stayAt(rail.stays, id, at);
      const lane = stay === undefined
        ? undefined
        : branchAt(rail.branches, stay.rootPlaceId, at)?.lane;
      if (pose !== null && lane !== undefined && running.some((other) => other < lane)) {
        tallest.set(lane, Math.max(tallest.get(lane) ?? 0, MARKER_DIAMETER / 2 - pose.dy));
      }
    }
  }
  return tallest;
}

/** Each line sits above the next by what the group on the lower one needs. */
function laneOffsets(rail: RailLayout, episode: readonly RailBranch[]): Record<string, number> {
  const startMs = Math.min(...episode.map((branch) => branch.startMs));
  const endMs = Math.max(...episode.map((branch) => branch.endMs));
  const tallest = tallestGroupByLane(rail, startMs, endMs);
  const gapAbove = (lane: number): number => Math.max(
    MIN_PITCH_PX,
    Math.round((tallest.get(lane) ?? 0) + LOCATIONS_ROOM_PX),
  );
  const lanes = episode.flatMap((branch) => [
    branch.lane,
    branch.forkFromLane ?? 0,
    branch.joinToLane ?? 0,
  ]);
  const offsets: Record<string, number> = { 0: 0 };
  for (let lane = -1, y = 0; lane >= Math.min(...lanes); lane -= 1) {
    y -= gapAbove(lane + 1);
    offsets[lane] = y;
  }
  for (let lane = 1, y = 0; lane <= Math.max(...lanes); lane += 1) {
    y += gapAbove(lane);
    offsets[lane] = y;
  }
  return offsets;
}

/** Rails only as far apart as the people and places between them need. */
export function withFittedLanePitch(rail: RailLayout): RailLayout {
  const offsetsById = new Map<string, Record<string, number>>();
  for (const episode of splitEpisodes(rail.branches)) {
    const offsets = laneOffsets(rail, episode);
    for (const branch of episode) {
      offsetsById.set(branch.id, offsets);
    }
  }
  return {
    ...rail,
    branches: rail.branches.map((branch) => {
      const offsets = offsetsById.get(branch.id);
      return offsets === undefined ? branch : { ...branch, laneOffsets: offsets };
    }),
  };
}
